<template>
    <div>
        <div ref="sourceContainer" class="lz-hidden">
            <slot />
        </div>
        <div ref="visibleContainer"></div>
        <collapse-transition>
            <div
                v-show="showOverflow"
                ref="overflowContainer">
            </div>
        </collapse-transition>
        <div v-if="showExpandButton || showCollapseButton" class="lz-mt-6 lz-flex lz-flex-col lz-items-center">
            <button
                class="lz-inline-flex lz-flex-col lz-items-center lz-font-bold lz-text-primary lz-bg-transparent lz-border-0 lz-p-0 lz-cursor-pointer lz-group"
                type="button"
                :aria-expanded="expanded ? 'true' : 'false'"
                @click="toggle">
                {{ expanded ? collapseLabel : expandLabel }}
                <lz-icon name="mini-arrow2" class="lz-stroke-primary lz-w-6 lz-h-6 lz-shrink-0 group-hover:lz-translate-y-2 lz-transition-transform lz-duration-300"/>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import CollapseTransition from '@ivanv/vue-collapse-transition/src/CollapseTransition.vue'
import { breakpoint } from '@/mobile'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import LzIcon from '@/components/base/LzIcon.vue'

const props = withDefaults(defineProps<{
    initialVisibleCount?: number;
    mobileInitialVisibleCount?: number;
    desktopInitialVisibleCount?: number;
    enableMobile?: boolean;
    enableDesktop?: boolean;
    expandLabel?: string;
    collapseLabel?: string;
    allowCollapse?: boolean;
}>(), {
    initialVisibleCount: 3,
    mobileInitialVisibleCount: undefined,
    desktopInitialVisibleCount: undefined,
    enableMobile: true,
    enableDesktop: true,
    allowCollapse: true,
})

const sourceContainer = ref<HTMLElement | null>(null)
const visibleContainer = ref<HTMLElement | null>(null)
const overflowContainer = ref<HTMLElement | null>(null)
const expanded = ref(false)
const isMobile = ref(false)
const items = ref<HTMLElement[]>([])

const currentEnabled = computed(() => isMobile.value ? props.enableMobile : props.enableDesktop)
const currentInitialVisibleCount = computed(() => {
    if (isMobile.value) {
        return props.mobileInitialVisibleCount ?? props.initialVisibleCount
    }

    return props.desktopInitialVisibleCount ?? props.initialVisibleCount
})
const canTruncate = computed(() => currentEnabled.value && items.value.length > currentInitialVisibleCount.value)
const showOverflow = computed(() => canTruncate.value && expanded.value)
const showExpandButton = computed(() => canTruncate.value && !expanded.value)
const showCollapseButton = computed(() => canTruncate.value && expanded.value && props.allowCollapse)

function collectItems(): void {
    if (!sourceContainer.value) {
        items.value = []
        return
    }

    items.value = Array.from(sourceContainer.value.children).filter((child): child is HTMLElement => child instanceof HTMLElement)
}

function clearContainer(container: HTMLElement | null): void {
    if (!container) {
        return
    }

    while (container.firstChild) {
        container.removeChild(container.firstChild)
    }
}

function appendItems(container: HTMLElement | null, nodes: HTMLElement[]): void {
    if (!container) {
        return
    }

    nodes.forEach(node => {
        container.appendChild(node)
    })
}

function renderItems(): void {
    isMobile.value = window.innerWidth < breakpoint

    clearContainer(visibleContainer.value)
    clearContainer(overflowContainer.value)

    if (!currentEnabled.value) {
        appendItems(visibleContainer.value, items.value)
        return
    }

    appendItems(visibleContainer.value, items.value.slice(0, currentInitialVisibleCount.value))
    appendItems(overflowContainer.value, items.value.slice(currentInitialVisibleCount.value))
}

function onResize(): void {
    renderItems()
}

function toggle(): void {
    if (expanded.value && !props.allowCollapse) {
        return
    }

    expanded.value = !expanded.value
}

onMounted(async () => {
    await nextTick()
    collectItems()
    renderItems()
    window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
})
</script>
